import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BadgeDollarSign, ClipboardCheck, Link2, Share2 } from "lucide-react";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminFetch } from "@/hooks/use-admin-fetch";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Marketing dashboard.
 *
 * Port of `src/app/admin/marketing/page.tsx` (legacy NextJS, ~310 LOC) per D21.
 *
 * Endpoints:
 *   - GET /api/admin/marketing/stats?country=XX
 *   - GET /api/admin/marketing/journeys
 *
 * Below the funnel, four cards lead to the surfaces that are not
 * lead-lifecycle stages: Social publishing, Go-live readiness, Paid ads and
 * Tracked links. Brand tokens only — no raw palette colours.
 *
 * There is no "delivery channels" card: the one this page carried listed
 * providers the stack does not use, all "configured", read from nothing. The
 * real providers and their state live on Marketing → Settings.
 */

interface Stats {
  total: number;
  leads: number;
  mql: number;
  sql: number;
  trials: number;
  paid: number;
  expansion: number;
  churned: number;
  sourceBreakdown: Record<string, number>;
  weeklyEvents: number;
  activeCampaigns: number;
  totalMessages: number;
  activeEnrollments: number;
  completedEnrollments: number;
}

interface Journey {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  _count: { steps: number; enrollments: number };
}

export const Route = createFileRoute("/_authenticated/marketing")({
  component: MarketingDashboardPage,
});

function MarketingDashboardPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const { visibleCountries: countries } = useAdminCountries();
  const { country: headerCountry, setCountry: setHeaderCountry } = useAdminContext();
  const selectedCountry = headerCountry ?? "";
  const setSelectedCountry = (code: string) => setHeaderCountry(code === "" ? null : code);
  const [stats, setStats] = useState<Stats | null>(null);
  const [journeys, setJourneys] = useState<Journey[]>([]);

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of countries) {
      if (c.totalProviders > 0) map[c.code] = c.totalProviders;
    }
    return map;
  }, [countries]);

  useEffect(() => {
    const params = selectedCountry ? `?country=${selectedCountry}` : "";
    Promise.all([
      adminFetch.get<Stats>(`/api/admin/marketing/stats${params}`).catch(() => null),
      adminFetch.get<Journey[]>("/api/admin/marketing/journeys").catch(() => []),
    ]).then(([s, j]) => {
      if (s) setStats(s);
      if (j) setJourneys(j);
    });
  }, [adminFetch, selectedCountry]);

  const kpis = [
    { label: t`Leads`, value: stats?.total ?? 0, icon: "🎯", color: "text-primary" },
    { label: "MQL", value: stats?.mql ?? 0, icon: "📊", color: "text-info-strong" },
    { label: "SQL", value: stats?.sql ?? 0, icon: "🔥", color: "text-accent-strong" },
    { label: t`Trials`, value: stats?.trials ?? 0, icon: "⏳", color: "text-warning-strong" },
    { label: t`Paid`, value: stats?.paid ?? 0, icon: "💳", color: "text-success-strong" },
    {
      label: t`Messages`,
      value: stats?.totalMessages ?? 0,
      icon: "✉️",
      color: "text-mint-strong",
    },
  ];

  const FUNNEL = [
    {
      key: "acquire",
      stage: t`ACQUIRE`,
      color: "border-info-border bg-info-soft",
      dotColor: "bg-info",
      items: [t`Leads DB`, t`Landing pages`, t`Ads tracking`, t`Referrals`],
      to: "/marketing-leads" as const,
      count: stats?.leads ?? 0,
    },
    {
      key: "nurture",
      stage: t`NURTURE`,
      color: "border-border bg-accent-soft",
      dotColor: "bg-accent",
      items: [t`Journey automation`, t`Email campaigns`, t`AI lead scoring`, t`Segments`],
      to: "/marketing-journeys" as const,
      count: stats?.activeEnrollments ?? 0,
    },
    {
      key: "convert",
      stage: t`CONVERT`,
      color: "border-warning-border bg-warning-soft",
      dotColor: "bg-warning",
      items: [t`14-day trials`, t`Demo`, t`Onboarding`, t`Activation`],
      to: "/marketing-campaigns" as const,
      count: stats?.trials ?? 0,
    },
    {
      key: "expand",
      stage: t`EXPAND`,
      color: "border-success-border bg-success-soft",
      dotColor: "bg-success",
      items: [t`Upsell triggers`, t`Referral program`, t`Retention`, t`Win-back`],
      to: "/marketing-journeys" as const,
      count: stats?.expansion ?? 0,
    },
  ];

  const HUB_LINKS = [
    {
      to: "/marketing-social" as const,
      icon: Share2,
      title: t`Social publishing`,
      description: t`Automated posts on our own channels — generate, review, schedule, publish.`,
    },
    {
      to: "/marketing-readiness" as const,
      icon: ClipboardCheck,
      title: t`Go-live readiness`,
      description: t`The three delivery switches and every check that must pass before real messages leave.`,
    },
    {
      to: "/marketing-ads" as const,
      icon: BadgeDollarSign,
      title: t`Paid ads`,
      description: t`Spend, B2B conversions and budget guardrails for Meta and Google — pause-only, nothing is created or edited.`,
    },
    {
      to: "/marketing-links" as const,
      icon: Link2,
      title: t`Tracked links`,
      description: t`Short links that follow the UTM naming convention, for every post, email and print piece.`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Direct Marketing</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Lead management, multi-channel campaigns, journey automation, AI scoring</Trans>
        </p>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={setSelectedCountry}
      />

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <span className="text-2xl">{s.icon}</span>
              <div className={`mt-1 text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-muted-foreground text-xs">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Funnel — ACQUIRE → NURTURE → CONVERT → EXPAND</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FUNNEL.map((s) => (
              <Link
                key={s.key}
                to={s.to}
                className={`rounded-xl border p-4 transition-all hover:shadow-md ${s.color}`}
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dotColor}`} />
                    <span className="text-foreground text-sm font-bold">{s.stage}</span>
                  </div>
                  <span className="text-foreground text-lg font-bold">{s.count}</span>
                </div>
                <ul className="space-y-1">
                  {s.items.map((item) => (
                    <li key={item} className="text-muted-foreground text-xs">
                      • {item}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Surfaces outside the lead funnel — owned channels, the go-live checks,
          paid ads and tracked links. Deliberately cards rather than more funnel
          stages: the funnel above is the lead lifecycle. */}
      <div className="grid gap-4 sm:grid-cols-2">
        {HUB_LINKS.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.to}
              to={card.to}
              className="border-border bg-card hover:border-border-strong flex min-h-11 flex-col justify-between gap-3 rounded-xl border p-4 transition-all hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <span className="bg-primary-soft text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="size-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <div className="text-foreground text-sm font-bold">{card.title}</div>
                  <p className="text-muted-foreground text-xs">{card.description}</p>
                </div>
              </div>
              <span className="text-muted-foreground self-end text-xs">
                <Trans>Open →</Trans>
              </span>
            </Link>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Lead sources</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { source: "scraping", label: t`Scraping import`, manual: false },
              { source: "claim", label: t`Profile claim`, manual: false },
              { source: "self_registration", label: t`Self registration`, manual: false },
              { source: "landing_page", label: t`Landing page`, manual: false },
              { source: "agent_referral", label: t`Agent referral`, manual: false },
              { source: "conference", label: t`Conferences`, manual: false },
              { source: "admin_manual", label: t`Manual (admin)`, manual: true },
              { source: "waitlist", label: t`Country waitlist`, manual: false },
            ].map((s) => (
              <div
                key={s.source}
                className="border-border/50 flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <div className="text-sm font-medium">{s.label}</div>
                  <Badge variant={s.manual ? "info" : "success"} className="text-[0.625rem]">
                    {s.manual ? <Trans>Manual</Trans> : <Trans>Organic</Trans>}
                  </Badge>
                </div>
                <span className="text-muted-foreground text-sm font-bold">
                  {stats?.sourceBreakdown?.[s.source] ?? 0}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Active journeys</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {journeys.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              <Trans>No journeys configured. Open Journeys to create automated sequences.</Trans>
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {journeys.map((j) => (
                <Link
                  key={j.id}
                  to="/marketing-journeys"
                  className="border-border/50 rounded-lg border p-4 transition-all hover:shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{j.name}</div>
                    <Badge variant={j.isActive ? "success" : "neutral"}>
                      {j.isActive ? <Trans>Active</Trans> : <Trans>Inactive</Trans>}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground mt-2 text-xs">
                    <Trans>
                      {j._count.steps} steps · {j._count.enrollments} enrollments
                    </Trans>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
